import {
	cloneDeep,
	set,
	isUndefined,
	isEmpty,
	isNull,
	map,
	get,
	isBoolean,
	has,
	isNumber,
	isString,
	forEach,
	indexOf,
	range,
	findIndex,
	differenceWith,
	isEqual,
} from 'lodash';
import moment from 'moment-timezone';
import momentDurationFormatSetup from 'moment-duration-format';
import 'custom-event-polyfill';
import base64ToBlob from 'base64toblob';
import JSZip from 'jszip';
import JSZipUtils from 'jszip-utils';
import FileSaver from 'file-saver';
import zxcvbn from 'zxcvbn';
import valid from 'card-validator';
import { B2C_USER_CANCELLED } from '@constants';
import formUtils from './formUtils';

momentDurationFormatSetup(moment);

const notificationTypeConst = {
	clockIn         : 14,
	clockOut        : 15,
	newLocation     : 16,
	pendingPayments : 17,
};

export default {
	...formUtils,

	/**
	 * Generic function to update a progress button
	 * to prevent repeated code, receive the following object structure
	 *
	 * params = {
	 *     context  : [component context (this)],
	 *     property : [pb property state],
	 *     value    : [new pb value],
	 *     callback : [(optional) function executed after update]
	 * }
	 *
	 * @param  {Object} params
	 */
	updateProgressButton({
		context,
		property,
		value,
		callback,
	}) {
		const state = cloneDeep(context.state);

		// Set value
		set(state, property, value);

		context.setState(state, () => {
			// Prevent clean up if it's loading
			if (value === 'loading') {
				if (!isUndefined(callback)) callback();

				return;
			}

			// Clean progressButton after a 800ms to let component finish the animation
			setTimeout(() => {
				set(state, property, '');

				context.setState(state, () => {
					if (!isUndefined(callback)) callback(state);
				});
			}, 800);
		});
	},

	/**
	 * Function to register an App Event Listener
	 *
	 * @param  {String}   eventIdentifier
	 * @param  {Function} callback
	 */
	registerEventListener(eventIdentifier, callback) {
		document.addEventListener(eventIdentifier, (event) => callback(event.detail, eventIdentifier));
	},

	/**
	 * Function to dispatch an event through the app
	 *
	 * @param  {String} eventIdentifier
	 * @param  {Object} parameters
	 */
	dispatchEvent(eventIdentifier, parameters) {
		document.dispatchEvent(new CustomEvent(eventIdentifier, { detail : parameters }));
	},

	notify(parameters) {
		this.dispatchEvent('notify', parameters);
	},

	dispatchUnauthorizedMessage() {
		this.dispatchEvent('unauthorized', {});
	},

	/**
	 * Function to format a phoneNumber string
	 *
	 * @param  {String} phoneNumber
	 * @return {String}
	 */
	formatPhoneNumber(phoneNumber) {
		// Prevent format and undefined value or empty value
		if (isUndefined(phoneNumber) || isEmpty(phoneNumber)) return '';

		// normalize string and remove all unnecessary characters
		return this.getDigits(phoneNumber)
			.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
	},

	/**
	 * Function to handle the API errors
	 *
	 * @param  {String}    message         [Error message to be displayed]
	 * @param  {String}    loadingProperty [Property to be updated, could be in dot notation, like: "a.b"]
	 * @param  {Object}    context         [Component Context]
	 * @param  {Function}  callback        [Callback function to be executed after update the state (optional)]
	 * @param  {String}    title           [Alert title]
	 * @param  {Boolean}   hideAlert           [Show or hide the alert]
	 */
	genericAPIErrorHandler(defaultMessage, error, loadingProperty, context, callback, defaultTitle = 'Oops', hideAlert = false) {
		const { response } = error;
		const isRequestFailed = isUndefined(response);
		let title = defaultTitle;
		let message = isRequestFailed ? 'Request Failed, please check your Internet connection' : defaultMessage;

		if (!isRequestFailed) {
			/**
			 * For catched failed promises, the response
			 * body is inside the `data` object.
			 * In the other hand, for error callback responses
			 * the response is inside the `body` object.
			 */
			const responseBodyKey = isUndefined(error.response.data) ? 'body' : 'data';

			/**
			 * Use error statusText from
			 * the response as title
			 * otherwise use defaultTitle
			 * instead
			 */
			title = get(error, ['response', 'statusText'], title);

			message = get(error, `response.${responseBodyKey}.ErrorMessage`);
			message = !message ? get(error, `response.${responseBodyKey}.Message`) : message;
			message = !message ? get(error, `response.${responseBodyKey}.message`) : message;
			message = !message ? get(error, 'message') : message;
			message = !message ? get(error, 'Message') : message;
		}

		if (!hideAlert) {
			this.notify({
				type : 'error',
				title,
				message,
			});
		}

		if (!isUndefined(loadingProperty) && !isUndefined(context)) {
			const state = cloneDeep(context.state);

			// Update property
			set(state, loadingProperty, false);

			context.setState(state, () => {
				if (!isUndefined(callback)) callback(state);
			});
		}
	},

	/**
	 * Function to convert an API response into a SelectOptions
	 * based on a given parameters
	 *
	 * @param  {Object}  parameters
	 * @param  {Object}  response
	 */
	buildSelectArrayOptionsFromAPIResponse({
		labelProperty, valueProperty, imageURL, subLabel, data,
	}) {
		const returnData = [];
		if (data.length) {
			forEach(data, (item) => {
				if (imageURL) {
					returnData.push({
						value    : item[valueProperty],
						label    : item[labelProperty],
						imageURL : item[imageURL],
						subLabel : item[subLabel],
					});
				} else {
					returnData.push({
						value : item[valueProperty],
						label : item[labelProperty],
					});
				}
			});
		}
		return returnData;
	},

	/**
	 * Function to handle on Field change, based on the given parameters
	 *
	 * The component that uses this generic function must have defined:
	 *
	 * - Form data as an object on States called "form"
	 * - Fields validation as an object on States called "validations"
	 * - onChange callbacks needs be called "postOnChangeCallbacks" and should
	 *   be defined globally in the component itself
	 * - The parameter "firstLevelProperty" is optional and is only used for
	 *   setting the value in "form" structure, is not being used on "postOnChangeCallbacks"
	 *
	 * @param  {String} property   [Property field name in form structure, could be in dot notation, like: "a.b"]
	 * @param  {String} type       [Field type]
	 * @param  {Object} context    [Component Context]
	 * @param  {Object} event      [Event itself]
	 */
	genericOnFieldChangeHandler(property, type, context, event) {
		const state = cloneDeep(context.state);
		let value;
		let validValue = false;

		switch (type) {
			case 'field':
				value = event.target.value;
				break;

			case 'select':
				value = event ? event.value : null;
				break;

			case 'checkbox':
				value = event.target.checked;
				break;

			case 'date':
			case 'value':
				value = event;
				break;

			default:
				value = '';
				break;
		}

		// Get last value from given property
		const lastValue = get(state.form, property, value);

		// Update property
		set(state.form, property, value);

		// Split property in case is recursive
		const propertySplitted = property.split('.');

		/**
		 * Clear out validation for current property, if there is one
		 * by checking first level property name also, just in case is validating a group of items
		 * or one by one using deeper property name with dot annotation string
		 */
		if (!isUndefined(state.validations)
		&& (has(state.validations, property)
		|| has(state.validations, propertySplitted[0]))) {
			set(state.validations, has(state.validations, property) ? property : propertySplitted[0], null);
		}

		// Check if it's a valid value to be used on the property postOnChangeCallback
		validValue = isBoolean(value);
		validValue = !validValue ? isNumber(value) : validValue;
		validValue = !validValue ? isString(value) && !isEmpty(value) : validValue;

		if (type === 'select' && value === null) validValue = true;
		// First update the state, and then run the callback function
		context.setState(state, () => {
			/**
			 * Execute a proper postOnChangeCallback if it's defined for the current property, and
			 * the value is not empty
			 */
			if (!isUndefined(context.postOnChangeCallbacks)
			&& has(context.postOnChangeCallbacks, property)
			&& validValue) {
				context.postOnChangeCallbacks[property](value, lastValue);
			}
		});
	},

	/**
	 * Function to get the digits of a String
	 *
	 * @param {String} value
	 * @return {String}
	 */
	getDigits(value) {
		if (!value) return value;

		return value.replace(/\D/g, '');
	},

	/**
	 * Function to download blob
	 *
	 * @param {Object} context
	 */
	downloadReport(response) {
		const blob = base64ToBlob(response.data.FileContents, response.data.ContentType);
		const fileName = response.data.FileDownloadName;

		// used for IE
		if (window.navigator.msSaveOrOpenBlob) window.navigator.msSaveOrOpenBlob(blob, fileName);
		else {
			const a = document.createElement('a');
			const url = window.URL.createObjectURL(blob);

			a.style.display = 'none';
			a.href = url;
			a.download = fileName;

			document.body.appendChild(a);

			a.click();

			window.URL.revokeObjectURL(url);
		}
	},

	/**
	 * Function to download when response is directly a blob
	 *
	 * @param {Object} context
	 * @param {Object} response
	 */
	downloadFromBlobResponse(data, fileName) {
		// used for IE
		if (window.navigator.msSaveOrOpenBlob) window.navigator.msSaveOrOpenBlob(data, fileName);
		else {
			const a = document.createElement('a');
			const url = window.URL.createObjectURL(data);

			a.style.display = 'none';
			a.href = url;
			a.download = fileName;

			document.body.appendChild(a);

			a.click();

			window.URL.revokeObjectURL(url);
		}
	},

	/**
	 * Function to download when response is directly a blob
	 *
	 * @param {Object} context
	 * @param {Object} response
	 */
	downloadReportFromBlobResponse(data, fileName) {
		return this.downloadFromBlobResponse(data, fileName);
	},

	/**
	 * Function to parse UTC to a given timeZone
	 *
	 * @param  {string} utcDate
	 * @param  {string} timeZone
	 * @return {Moment}
	 */
	parseUTCDateToTimeZone(utcDate, timeZone) {
		return moment.utc(utcDate).tz(timeZone);
	},

	/**
	 * Device/Browser detection
	 */
	getBrowserInfo() {
		const { navigator : { userAgent, vendor, maxTouchPoints } } = window;

		const isAppleDevice = vendor.toLowerCase().includes('apple');
		const isSafari = isAppleDevice && (/safari/gi).test(userAgent);
		const isIPhone = isAppleDevice && (/iphone/gi).test(userAgent);
		const isIPad = isAppleDevice && !isIPhone && maxTouchPoints > 0;
		const isDesktopSafari = isSafari && !isIPhone && !isIPad;
		const isIOSDevice = isIPhone || isIPad;

		return {
			isAppleDevice, isSafari, isIPhone, isIPad, isDesktopSafari, isIOSDevice,
		};
	},

	/**
	 * Function to download files
	 *
	 * @param {Array}   filesToDownload
	 * @param {Strings} downloadFileName
	 */
	downloadFiles(filesToDownload, downloadFileName = 'download.zip') {
		return new Promise((resolve, reject) => {
			const zip = new JSZip();

			const fetchFiles = () => (new Promise((_resolve, _reject) => {
				const promises = map(filesToDownload, ({ fileName, url }) => (new Promise((resolveRead, rejectRead) => {
					JSZipUtils.getBinaryContent(url, (error, data) => {
						if (error) return rejectRead(error);

						zip.file(fileName, data, url);

						return resolveRead();
					});
				})));

				Promise.all(promises)
					.then(_resolve)
					.catch(_reject);
			}));

			const generateZip = () => (new Promise((_resolve, _reject) => {
				zip.generateAsync({ type : 'blob' })
					.then(_resolve)
					.catch(_reject);
			}));

			const downloadFile = (content) => {
				FileSaver.saveAs(content, downloadFileName);

				return Promise.resolve();
			};

			fetchFiles()
				.then(generateZip)
				.then(downloadFile)
				.then(resolve)
				.catch(reject);
		});
	},

	/**
	 * Function to calculate password strength based on zxcvbn
	 *
	 * @param {String} value
	 */
	calculatePasswordScore(value) {
		return zxcvbn(value).score;
	},

	/**
	 * Function to format number to currency
	 *
	 * @param {String, Number}  value
	 * @param {Boolean} isDecimalRequired
	 *
	 * @return String
	 */
	currencyFormatter(value = NaN, isDecimalRequired = true) {
		const parsedValue = isString(value) ? parseFloat(value) : value;

		if (!parsedValue || Number.isNaN(parsedValue)) return isDecimalRequired ? '$0.00' : '$0';

		const amount = parsedValue.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');

		return `$${isDecimalRequired ? amount : amount.substr(0, amount.indexOf('.'))}`;
	},

	updateStateGeneric({ state, property, value }) {
		const newState = cloneDeep(state);

		// Update state
		set(newState, property, value);

		return newState;
	},

	/**
	 * If any 1 of the user type (given in the array) matches with the user type (in the user object)
	 * the function returns 'true'.
	 *
	 * @export
	 * @param {Object} user
	 * @param {Array} userTypeArray
	 * @returns Boolean
	 */
	IsUserType(userTypeId, allowedTypes) {
		return indexOf(allowedTypes, userTypeId) > -1;
	},

	/**
	 * Returns the actions that can be performed on the job object by the given user
	 * @param {Object} user
	 * @param {Object} jobEventObject
	 */
	getJobActionPermissions(user, jobEventObject) {
		let allowEdit = false;
		let allowPost = false;
		let allowRemovePost = false;
		let allowDelete = false;
		let allowRestore = false;
		let allowDuplicate = true;
		let allowAssign = false;
		let allowChange = false;
		let allowUnassign = false;
		let allowAddNotes = false;
		let allowDeleteAbandonedJob = false;

		const organizationId = get(user, ['Organization', 'Id']);
		const isJobFilled = jobEventObject.ApplicantsNeeded === jobEventObject.ApplicantsAssigned;

		if (!(user.isClient() || user.isLEA()) || (user.isLEA() && jobEventObject.AgencyId === organizationId)) {
			if (!jobEventObject.Deleted) {
				// An "abandoned" job can be deleted only when it has no assigned officers
				// or when the assigned officers didn't clock-in
				allowDeleteAbandonedJob = (
					jobEventObject.ApplicantsAssigned === 0
					|| !jobEventObject.Jobs
						.filter((item) => item.AssignedOfficers && item.AssignedOfficers.length > 0)
						.reduce((acc, item) => [...acc, ...item.AssignedOfficers], [])
						.some((item) => !isNull(item.ClockIn))
				);

				allowEdit = true;

				if (!jobEventObject.IsClosed) {
					allowPost = !jobEventObject.Posted;
					allowRemovePost = jobEventObject.Posted;
					allowDelete = true;
				}

				if (user.isSuperAdmin()) allowEdit = false;
			}

			// Allow assign if the job is not filled.
			// TODO: How to validate jobs independently?
			allowAssign = !isJobFilled;
			allowChange = true;
		}

		allowRestore = jobEventObject.Deleted;
		allowDuplicate = !user.isSuperAdmin() && jobEventObject.AgencyId === organizationId;
		allowAddNotes = !user.isClient();
		allowUnassign = user.isLEA() || user.isCoordinator() || user.isAgencyAdmin();

		if (user.isLEA()) {
			const { AgencyId } = jobEventObject;
			allowAddNotes = (AgencyId === organizationId);
		}

		return {
			allowEdit,
			allowPost,
			allowRemovePost,
			allowDelete,
			allowRestore,
			allowDuplicate,
			allowAssign,
			allowChange,
			allowUnassign,
			allowAddNotes,
			allowDeleteAbandonedJob,
			allowViewHistory : true,
		};
	},

	/**
	 * Function to concatenate Address, City,
	 * State and Zip values
	 *
	 * @param {String} param.address
	 * @param {String} param.city
	 * @param {String} param.state
	 * @param {String} param.zip
	 * @param {Bool} multilined
	 *
	 * @returns {String}
	 */
	concatenateAddressDetails({
		address,
		city,
		state,
		zip,
		latitude,
		longitude,
	}, { useMultiline = false } = {}) {
		let top = '';
		let bottom = '';

		// Add Address to result
		if (address) top += address;

		// Add City to result
		if (city && !isEmpty(top) && !useMultiline) bottom += `, ${city}`;
		else if (city && !isEmpty(top)) bottom += city;

		// Add State to result
		if (state && !isEmpty(bottom)) bottom += `, ${state}`;
		else if (state && isEmpty(bottom)) bottom += state;

		// Add Zip to result
		if (zip && state) bottom += ` ${zip}`;
		else if (zip && !state && !isEmpty(bottom) && !useMultiline) bottom += `, ${zip}`;
		else if (zip && isEmpty(bottom)) bottom += zip;

		if (isEmpty(top + bottom) && useMultiline) {
			return {
				top    : isNull(latitude) ? '' : `Lat: ${latitude}`,
				bottom : isNull(longitude) ? '' : `Lng: ${longitude}`,
			};
		}

		// Return object if multiline address is needed
		if (useMultiline) return { top, bottom };

		if (!isEmpty(top + bottom)) return top + bottom;

		return `lat ${latitude}, lng ${longitude}`;
	},

	abbreviateNumber(n) {
		const number = +parseFloat(n).toFixed(2);
		const SI_SYMBOL = ['', 'k', 'M', 'G', 'T', 'P', 'E'];

		/*
			Default values for scale and suffix if the method is unable to determine an abbreviated value.
			These values will also be used if the parsed number is NaN
		*/
		let scaled = 0;
		let suffix = '';

		// Iterate through the abbreviations to find the most appropriate one for the given number
		for (let i = 0; i < SI_SYMBOL.length; i++) {
			/*
				Criteria for abbreviating are as follows:
				- Dividing the number by 1000 ** i is greater than/equal to 1
				- Dividing the number by 1000 ** (i+1) is less than 1
			*/
			const thisExp = 1000 ** i;
			const nextExp = 1000 ** (i + 1);
			if (number / thisExp >= 1 && number / nextExp < 1) {
				// Scale and suffix determined, break the loop
				scaled = number / thisExp;
				suffix = SI_SYMBOL[i];
				break;
			}
		}

		// Format number and add suffix. Using this format guarantees that a string is always returned
		return +scaled.toFixed(1) + suffix;
	},

	processCardNumber(cardNumber) {
		return valid.number(cardNumber);
	},

	buildCardNumberMask({ lengths, gaps }) {
		const dataArray = [];
		const cardLength = Math.min(...lengths) + gaps.length;
		const loopArray = range(1, cardLength + 1);
		const gapsArray = gaps.map((item, index) => item + index + 1);
		forEach(loopArray, (item) => {
			if (item === 1) dataArray.push(/[0-9]/);
			else if (findIndex(gapsArray, (gapsItem) => gapsItem === item) === -1) dataArray.push(/\d/);
			else dataArray.push(' ');
		});
		return dataArray;
	},

	buildCvvMask({ code: { size } }) {
		const dataArray = [/[0-9]/];
		const loopArray = range(1, size);
		forEach(loopArray, () => {
			dataArray.push(/\d/);
		});
		return dataArray;
	},

	constructAddress(streetAddress1, streetAddress2, city, state, zip) {
		return `${`${streetAddress1},` || ''} ${`${streetAddress2},` || ''} ${`${city},` || ''} ${`${state},` || ''} ${zip || ''}`;
	},

	processMinimalLocationsData(data) {
		const returnData = [];
		forEach(data, (item) => {
			returnData.push({
				value : item.Id,
				label : item.Name,
			});
		});
		return returnData;
	},

	processNotificationPreferences(notificationsObj) {
		return map(notificationsObj, (value, key) => (
			{
				NotificationTypeId : notificationTypeConst[key],
				IsEmail            : value.email,
				IsSms              : value.sms,
			}
		));
	},

	queryStringDiff(currentQuery, filterArray) {
		const newFilterArray = Object.keys(currentQuery);
		return differenceWith(filterArray, newFilterArray, isEqual);
	},

	getJobStatusFromStatusId(statusId, status) {
		const result = { value : status };
		if ((statusId === 12) || (statusId === 2) || (statusId === 3) || (statusId === 4) || (statusId === 5)) result.label = 'warning';
		else if ((statusId === 7) || (statusId === 8) || (statusId === 9)) result.label = 'danger';
		else result.label = 'success';
		return result;
	},

	constructShortAddress(city, state) {
		let shortAddress = '';
		if (city && state) shortAddress = `${city},${state}`;
		else if (city && !state) shortAddress = city;
		else if (!city && state) shortAddress = state;
		return shortAddress;
	},

	formatStatusHistoryData(statusHistoryData, timeZoneId) {
		const returnData = [];
		if (!statusHistoryData.length) return [];
		forEach(statusHistoryData, (item) => {
			returnData.push({
				id    : item.Id,
				label : this.createStatusHistoryText(item.StatusId, item.Status, item.UpdatedBy),
				value : this.parseUTCDateToTimeZone(item.DateTime, timeZoneId).format('MMM D, YYYY | h:mm A'),
			});
		});
		return returnData;
	},

	createStatusHistoryText(statusId, status, userName) {
		let invoiceText = '';
		if ((statusId === 1) || (statusId === 3) || (statusId === 6) || (statusId === 9)) {
			invoiceText = userName
				? `${userName} ${status} invoice`
				: `Invoice was ${status}`;
		}
		if ((statusId === 12) || (statusId === 2) || (statusId === 4) || (statusId === 5) || (statusId === 7)) {
			invoiceText = userName
				? `${userName} changed invoice status to ${status}`
				: `Invoice status was changed to ${status}`;
		}
		if (statusId === 8) invoiceText = userName ? `${userName} filed a dispute for invoice` : 'Invoice was disputed';
		return invoiceText;
	},

	downloadAllFunction(downloadAllArray) {
		return new Promise((resolve, reject) => {
			let count = 0;
			const zip = new JSZip();
			forEach(downloadAllArray, ({ fileName, url }) => {
				JSZipUtils.getBinaryContent(url, (err, data) => {
					if (err) reject(err);
					else {
						zip.file(fileName, data, url);
						count++;
						if (count === downloadAllArray.length) {
							zip.generateAsync({ type : 'blob' }).then((content) => {
								FileSaver.saveAs(content, 'rollKallAttachment.zip');
								resolve(true);
							});
						}
					}
				});
			});
		});
	},

	getPaymentListTotalForCheckbox(paymentData) {
		let count = 0;
		forEach(paymentData, (item) => {
			if (item.StatusId !== 5) count++;
		});
		return count;
	},

	formatCreditCardType(type) {
		if (!type) return '';
		if (type === 'JCB') return type.toLowerCase();
		return type.replace(' ', '').split(/(?=[A-Z])/).join('-').toLowerCase();
	},

	setPaymentMethods({ BankAccounts, PaymentCards, DefaultSource }) {
		const bankAccounts = (BankAccounts || []).map((account) => ({
			id        : account.BankAccountId,
			type      : 'bank_account',
			bankName  : account.BankName,
			accNumber : account.AccountNumber.substr(account.AccountNumber.lastIndexOf('*') + 1),
			accStatus : account.BankAccountStatus,
			isDefault : account.BankAccountId === DefaultSource,
		}));

		const cards = (PaymentCards || []).map((card) => ({
			id         : card.PaymentCardId,
			type       : 'card',
			isDefault  : card.IsDefaultCard,
			cardNumber : card.CardNumber.replace(/[.]/g, '*'),
			cardType   : this.formatCreditCardType(card.CardType),
			expiry     : `${card.ExpirationMonth.toString().length === 1
				? `0${card.ExpirationMonth}`
				: card.ExpirationMonth}/${card.ExpirationYear.toString().substr(2)}`,
		}));
		return [...bankAccounts, ...cards];
	},

	/**
	 * Check if AD error reason is cancellation
	 * @param {Error} error Error return from msalInstance
	 * */
	isADCancellation(error) {
		return error instanceof Error && error.message.includes(B2C_USER_CANCELLED);
	},

	encodeADState(state) {
		return window.btoa((JSON.stringify(state)));
	},

	decodeADState(encodedState) {
		if (!encodedState) return {};
		return JSON.parse(window.atob(encodedState));
	},
};
